// Web Share is not everywhere: Firefox, Chrome on Linux and older desktop
// Safari expose no `navigator.share`, so a share button rendered unconditionally
// throws `navigator.share is not a function` on tap. Callers gate the control
// on this check rather than on a desktop/mobile split — Chrome on Windows and
// ChromeOS does have it.
export const isShareSupported = (): boolean =>
  typeof navigator.share === "function";

/**
 * `navigator.share` with the user's own cancellation swallowed. Resolves
 * `true` when the share went through, `false` when the sheet was dismissed —
 * the one thing the API tells a caller that reports shares (SHO-757).
 *
 * Closing the share sheet rejects with an `AbortError` ("Abort due to
 * cancellation of share." on WebKit, "Share canceled" on Chromium). Left
 * uncaught, every dismissal lands in Sentry as an unhandled rejection. Only
 * that case is swallowed: an invalid URL or a denied permission is a real
 * failure and keeps surfacing.
 */
export const share = (data: ShareData): Promise<boolean> =>
  navigator.share(data).then(
    () => true,
    (error: unknown) => {
      if (error instanceof DOMException && error.name === "AbortError")
        return false;
      throw error;
    },
  );
